import {
  Box,
  createStyles, Grid, makeStyles, MenuItem, OutlinedInput, Select, Theme, useMediaQuery, useTheme, FormHelperText,
} from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useFormContext, Controller, ControllerRenderProps } from 'react-hook-form';

interface IDesiredTimeArray {
  id: number;
  label: string;
  month: string;
  day: string;
  hour: string;
}

interface IFormElementDateSelect {
  handleChangeTimeByDate?: (month:string, day: string, index:number) => void,
  handleChangeDesiredMonth?: (month:string, index:number) => void,
  defaultDesiredTimeList?: [string[], string[], string[]],
  monthsToShow?: string[],
  desiredDayList?: [string[], string[], string[]],
}


const FormElementThreeDateSelect : React.FC<IFormElementDateSelect> = ({
  handleChangeTimeByDate, handleChangeDesiredMonth, defaultDesiredTimeList = [[], [], []],
  monthsToShow = [], desiredDayList = [[], [], []],
}) => {
  const { getValues, control, errors } = useFormContext();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const desiredTimeArray : IDesiredTimeArray[] = [
    {
      id: 1, label: '第一希望', month: 'desiredMonth1', day: 'desiredDay1', hour: 'desiredHour1',
    },
    {
      id: 2, label: '第二希望', month: 'desiredMonth2', day: 'desiredDay2', hour: 'desiredHour2',
    },
    {
      id: 3, label: '第三希望', month: 'desiredMonth3', day: 'desiredDay3', hour: 'desiredHour3',
    },
  ];

  return (
    <Grid container={true} spacing={2} className={classes.formElementControl}>
      <Grid item={true} xs={12} md={4}>
        <div className={classes.formLabelSelect}>
          <Box marginTop="26px">
            <p>
              ご希望の日時
            </p>
          </Box>
          <Box marginTop="13.5px">
            <div className={classes.labelNote}>
              ※希望の日時をお伺いするものであり、予約が確定するものではございません。
            </div>
          </Box>
        </div>
      </Grid>
      <Grid item={true} xs={12} md={5}>
        {desiredTimeArray.map((desiredTime: IDesiredTimeArray, index:number) => {
          const monthOptions = desiredTime.id === 1 ? [getValues(`${desiredTime.month}`)].filter(Boolean) : monthsToShow;
          const dayOptions = desiredTime.id === 1 ? [getValues(`${desiredTime.day}`)].filter(Boolean) : (desiredDayList[index] || []);
          return (
            <div key={desiredTime.id} className={classes.timeContainer}>
              <div className={classes.formLabelDate}>
                <p>{desiredTime.label}</p>
              </div>
              <div className={classes.formSelectContainer}>
                <Controller
                  name={desiredTime.month}
                  control={control}
                  render={({ ref, ...rest }: ControllerRenderProps) => (
                    <Select
                      {...rest}
                      inputRef={ref}
                      className={clsx(classes.selectComponent, classes.monthSelect)}
                      displayEmpty={true}
                      disabled={desiredTime?.id === 1}
                      input={(<OutlinedInput classes={{ notchedOutline: classes.notchedOutline }} />)}
                      error={!!errors[`${desiredTime.month}`]}
                      onChange={(e) => {
                        rest?.onChange(e);
                        handleChangeDesiredMonth && handleChangeDesiredMonth(String(e.target.value), index);
                        handleChangeTimeByDate && handleChangeTimeByDate(String(e.target.value), getValues(`${desiredTime.day}`), index);
                      }}
                    >
                      {
                    [
                      <MenuItem key="empty" aria-label="None" value="">---</MenuItem>,
                      monthOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      )),
                    ]
                  }
                    </Select>
                  )}
                />
                <div className={classes.selectMiddleLabel}>
                  <p>月</p>
                </div>
                <Controller
                  name={desiredTime.day}
                  control={control}
                  render={({ ref, ...rest }: ControllerRenderProps) => (
                    <Select
                      {...rest}
                      inputRef={ref}
                      className={clsx(classes.selectComponent, classes.daySelect)}
                      displayEmpty={true}
                      disabled={desiredTime?.id === 1}
                      input={(<OutlinedInput classes={{ notchedOutline: classes.notchedOutline }} />)}
                      error={!!errors[`${desiredTime.day}`]}
                      onChange={(e) => {
                        rest?.onChange(e);
                        handleChangeTimeByDate && handleChangeTimeByDate(getValues(`${desiredTime.month}`), String(e.target.value), index);
                      }}
                    >
                      {
                    [
                      <MenuItem key="empty" aria-label="None" value="">---</MenuItem>,
                      dayOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      )),
                    ]
                  }
                    </Select>
                  )}
                />
                <div className={classes.selectMiddleLabel}>
                  <p>日</p>
                </div>
                <Controller
                  name={desiredTime.hour}
                  control={control}
                  render={({ ref, ...rest }: ControllerRenderProps) => (
                    <Select
                      {...rest}
                      inputRef={ref}
                      className={clsx(classes.selectComponent, classes.timeSelect)}
                      displayEmpty={true}
                      disabled={desiredTime?.id === 1}
                      input={(<OutlinedInput classes={{ notchedOutline: classes.notchedOutline }} />)}
                      error={!!errors[`${desiredTime.hour}`]}
                    >
                      {
                    [
                      <MenuItem key="empty" aria-label="None" value="">---</MenuItem>,
                      defaultDesiredTimeList[index]?.length > 0 && defaultDesiredTimeList[index]?.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      )),
                    ]
                  }
                    </Select>
                  )}
                />
              </div>
              <FormHelperText error={!!errors[`desired${desiredTime?.id}`]}>{errors[`desired${desiredTime?.id}`]?.message}</FormHelperText>
            </div>
          );
        })}
        <FormHelperText error={!!errors?.desiredDuplicate}>{errors?.desiredDuplicate?.message}</FormHelperText>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  formElementControl: {
    marginBottom: '29px',
    marginLeft: '10px',
    width: 'calc(100% - 55px)',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
      width: '100%',
    },
  },
  formLabelSelect: {
    backgroundColor: '#F4F5F5',
    width: '100%',
    display: 'flex',
    // justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: 'column',
    height: '460px',
    [theme.breakpoints.down('sm')]: {
      height: '150px',
    },
    '& p': {
      fontSize: '18px',
      lineHeight: '26px',
      color: '#6F7878',
      letterSpacing: '0.8px',
      fontWeight: 400,
      paddingLeft: '12px',

    },
  },
  formSelectContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  },
  selectComponent: {
    width: '100%',
    borderRadius: '5px',
    cursor: 'pointer',
    fontFamily: 'Noto Sans JP',
    fontSize: '18px',
    fontWeight: 500,
    // lineHeight: '26px',
    letterSpacing: '0.8px',
    color: '#606060',
  },

  optionComponent: {
    fontWeight: 'normal',
    display: 'block',
    whiteSpace: 'nowrap',
    minHeight: '1.2em',
    padding: ' 0px 2px 1px',
  },
  yearSelect: {
    fontSize: '16px',
    width: '80px',
    lineHeight: '19px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '71px',
    },
  },
  monthSelect: {
    fontSize: '16px',
    width: '71px',
    lineHeight: '19px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '75px',
    },
  },
  daySelect: {
    fontSize: '16px',
    width: '71px',
    lineHeight: '19px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '75px',
    },
  },
  timeSelect: {
    fontSize: '16px',
    width: '100%',
    maxWidth: '100px',
    lineHeight: '19px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '90px',
    },
  },
  selectMiddleLabel: {
    fontSize: '18px',
    lineHeight: '26px',
    margin: 'auto 16px',
  },
  formLabelDate: {
    backgroundColor: '#F4F5F5',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '56px',
    marginBottom: '10px',
    '& p': {
      fontSize: '18px',
      lineHeight: '26px',
      color: '#6F7878',
      letterSpacing: '0.8px',
      fontWeight: 400,
      textAlign: 'center',
      paddingLeft: '12px',
      '& span': {
        fontSize: '14px',
        lineHeight: '21px',
        color: '#ffffff',
        backgroundColor: '#d0111b',
        marginRight: '0.3em',
      },
    },
  },
  timeContainer: {
    marginBottom: '24px',
  },
  labelNote: {
    color: '#CD0021',
    fontSize: '16px',
    lineHeight: '24px',
    paddingLeft: '12px',
    paddingRight: '16px',
  },
  notchedOutline: {
    // NOTE: the legend element is a child of the notchedOutline component
    '& legend': {
      maxWidth: '1000px',
    },
  },
}));

export default FormElementThreeDateSelect;
